import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Bell, CheckCheck, X } from 'lucide-react';
import { auth, db } from '../../lib/firebase';
import {
  collection, query, where, orderBy, limit, onSnapshot,
} from 'firebase/firestore';

interface AppNotification {
  id: string;
  recipientId: string;
  type?: string;
  action?: string;
  eventId?: string;
  title: string;
  body?: string;
  read?: boolean;
  createdAt: string;
}

/**
 * Real-time in-app notification bell. Subscribes to the `notifications`
 * collection (server-written) for the current user and shows unread counts
 * + a dropdown with mark-read actions. Uses Firestore onSnapshot so new
 * notifications appear instantly without a refresh.
 */
export default function NotificationCenter({ align = 'right' }: { align?: 'left' | 'right' }) {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bellRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  // Ids the user has marked read in this session. Overrides snapshot data so a
  // slow/failed Firestore write (or a snapshot refire) never visually reverts
  // a notification back to unread.
  const locallyReadRef = useRef<Set<string>>(new Set());
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Compute the dropdown's fixed position from the bell button. Rendered via a
  // portal to document.body so the global header's backdrop-filter stacking
  // context can never trap or obscure it.
  const updatePos = () => {
    const rect = bellRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      top: rect.bottom + 8,
      left: align === 'right' ? Math.max(8, rect.right - 320) : Math.max(8, rect.left),
    });
  };

  useEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener('resize', updatePos);
    document.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      document.removeEventListener('scroll', updatePos, true);
    };
  }, [open, align]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(30)
    );
    const unsub = onSnapshot(q, (snap) => {
      const list: AppNotification[] = [];
      snap.forEach((d) => {
        const data = d.data() as AppNotification;
        list.push({ id: d.id, ...data, read: data.read || locallyReadRef.current.has(d.id) });
      });
      setItems(list);
      setUnread(list.filter(n => !n.read).length);
    }, (err) => {
      console.warn('Notification listener error:', err);
    });
    return unsub;
  }, []);

  // Close on outside click (checks both the bell wrapper and the portaled panel).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideBell = panelRef.current?.contains(target) ?? false;
      const insideDropdown = dropdownRef.current?.contains(target) ?? false;
      if (!insideBell && !insideDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Persist via the server (Admin SDK) with an ownership check so a read
  // notification stays read after a reload — client Firestore rule quirks can
  // silently reject the write.
  const persistRead = async (id: string) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`/api/notifications/${id}/read`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch { /* best-effort — local state stays read */ }
  };

  const handleNotificationClick = async (n: AppNotification) => {
    locallyReadRef.current.add(n.id);
    setItems(prev => prev.map(item => item.id === n.id ? { ...item, read: true } : item));
    setUnread(u => Math.max(0, u - 1));
    void persistRead(n.id);
    setOpen(false);

    // Deep-link to target module tab
    const typeStr = (n.type || n.action || n.title || '').toLowerCase();
    let targetTab = '';
    if (typeStr.includes('ticket') || typeStr.includes('support')) {
      targetTab = 'support';
    } else if (n.eventId || typeStr.includes('event') || typeStr.includes('schedule') || typeStr.includes('interview')) {
      targetTab = 'my-events';
    } else if (typeStr.includes('application') || typeStr.includes('document') || typeStr.includes('placement') || typeStr.includes('visa')) {
      targetTab = 'application';
    }
    if (targetTab) {
      window.dispatchEvent(new CustomEvent('nav:change', { detail: { tab: targetTab } }));
    }
  };

  const markAllRead = async () => {
    const unreadItems = items.filter(n => !n.read);
    unreadItems.forEach(n => locallyReadRef.current.add(n.id));
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch('/api/notifications/mark-all-read', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch { /* best-effort */ }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        ref={bellRef}
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-lg text-slate-500 hover:text-brand hover:bg-brand-active transition-all cursor-pointer"
        title="Notifications"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && pos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="dropdown-portal w-80 max-w-[85vw] bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden"
          style={{ top: pos.top, left: pos.left, zIndex: 'var(--z-dropdown)' }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/60">
            <span className="text-xs font-black text-slate-700 uppercase tracking-wider">Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} className="flex items-center gap-1 text-[10px] font-bold text-brand hover:text-brand-dark cursor-pointer">
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto overscroll-contain">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-slate-400">No notifications yet</div>
            ) : (
              items.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-blue-50/50 transition-all cursor-pointer flex items-start gap-2 ${n.read ? 'opacity-60' : ''}`}
                >
                  <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.read ? 'bg-transparent' : 'bg-rose-500'}`} />
                  <span className="min-w-0">
                    <span className="block text-xs font-bold text-slate-800">{n.title}</span>
                    {n.body && <span className="block text-[11px] text-slate-500 mt-0.5 leading-snug">{n.body}</span>}
                    <span className="block text-[10px] text-slate-400 mt-1">
                      {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
        ,
        document.body
      )}
    </div>
  );
}
